import { makeZip } from 'client-zip';
import { sanitizeFileName, type DownloadFilesResult, type ZipFileEntry } from './downloadFilesAsZip';
import {
  createUrlResolver,
  openResumableEntry,
  reconnectTracker,
  type BulkFetchOptions,
  type OpenedEntry,
} from './resumableDownload';

/**
 * zip 一括ダウンロードの「ディスクへ直接書き出す」版。
 *
 * JSZip 版（downloadFilesAsZip）は zip 全体をメモリ上で組み立てるため、数 GB を
 * 超えるフォルダ（音源納品など）ではブラウザのメモリ上限・4 GB 上限（ZIP64 非対応）で
 * 失敗する。こちらは File System Access API で選ばせた保存先へ、client-zip の
 * ストリーム（ZIP64 対応）をそのまま流し込むので、サイズに上限がない。
 * 対応ブラウザは Chrome / Edge（`showSaveFilePicker`）。
 */

/** 同時に取得を始めておくファイル数（先読み）。書き込みは 1 ファイルずつ順番に行う */
const PREFETCH = 3;

interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: { description?: string; accept: Record<string, string[]> }[];
}

interface WindowWithSavePicker {
  showSaveFilePicker?: (options?: SaveFilePickerOptions) => Promise<FileSystemFileHandle>;
}

/** ディスクへ直接書き出す方式が使えるブラウザか */
export const canStreamZipToDisk = (): boolean =>
  typeof window !== 'undefined' &&
  typeof (window as unknown as WindowWithSavePicker).showSaveFilePicker === 'function';

/**
 * 保存先を選ばせて書き込み先を返す。キャンセル時は null。
 * ユーザー操作（クリック）の直後に呼ぶこと（間に await を挟むと拒否される）。
 */
export async function pickZipSaveTarget(zipName: string): Promise<FileSystemWritableFileStream | null> {
  const picker = (window as unknown as WindowWithSavePicker).showSaveFilePicker;
  if (!picker) return null;
  try {
    const handle = await picker({
      suggestedName: sanitizeFileName(zipName),
      types: [{ description: 'ZIP', accept: { 'application/zip': ['.zip'] } }],
    });
    return await handle.createWritable();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') return null;
    throw error;
  }
}

type ZipInput = { name: string; input: ReadableStream<Uint8Array>; lastModified?: Date };

/**
 * entries を zip にまとめて writable へ書き出す（書き出し後に writable は閉じられる）。
 * 取得を始められなかったファイルはスキップし、成功 / 失敗件数を返す。
 * 受信の途中で切れたファイルは続きから取り直す（resumableDownload）。
 */
export async function streamFilesAsZip(
  entries: ZipFileEntry[],
  writable: FileSystemWritableFileStream,
  options?: BulkFetchOptions,
): Promise<DownloadFilesResult> {
  const total = entries.length;
  const succeededFileIds: string[] = [];
  let succeeded = 0;
  let failed = 0;
  let processed = 0;

  const resolver = createUrlResolver(options?.refreshEntries);
  const onReconnect = reconnectTracker(options?.onReconnectingChange);
  const open = (entry: ZipFileEntry) => openResumableEntry(entry, resolver, { onReconnect });

  async function* inputs(): AsyncGenerator<ZipInput> {
    const queue: { entry: ZipFileEntry; input: Promise<OpenedEntry | null> }[] = [];
    let next = 0;
    const enqueue = () => {
      if (next >= entries.length) return;
      const entry = entries[next];
      next += 1;
      queue.push({ entry, input: open(entry) });
    };
    for (let i = 0; i < PREFETCH; i += 1) enqueue();

    while (queue.length > 0) {
      const { entry, input } = queue.shift()!;
      enqueue();
      const resolved = await input;
      processed += 1;
      if (resolved) {
        succeeded += 1;
        if ('file' in entry && entry.file.id) succeededFileIds.push(entry.file.id);
        options?.onProgress?.(processed, total);
        yield { name: entry.path, input: resolved.stream, lastModified: resolved.lastModified };
      } else {
        failed += 1;
        options?.onProgress?.(processed, total);
      }
    }
  }

  await makeZip(inputs()).pipeTo(writable);
  return { total, succeeded, failed, succeededFileIds };
}
